import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
// Import the fallback image
import FallbackLogo from '@/assets/logo/college-hai-white-bg-logo.jpg';
import { useGlobalContext } from '@/context/GlobalContext';

import Button from '@/components/ui/components/Button';

// import { useGlobalContext } from "@/src/context/GlobalContext";

interface CollegeListItemProps {
  readonly college: any;
  readonly id: string;
  readonly className?: string;
  readonly isAbroadCollege?: boolean;
}

export default function CollegeListItem({ college, id, className = '', isAbroadCollege }: CollegeListItemProps) {
  const { isLeadFormOpen, setIsLeadFormOpen, setLeadFormData } = useGlobalContext();

  const handleOpen = () => {
    setIsLeadFormOpen(!isLeadFormOpen);
    setLeadFormData({
      source: `college`,
      college: college.id ?? null,
      title: college?.college_name ?? null,
      isAbroadCountry: college?.country?.country_name ? college.country.country_name !== 'India' : false,
    });
  };

  const isOnlineCollege = college?.is_online_college;

  const navbar =
    Array.isArray(college?.college_navbars) &&
    college.college_navbars.length > 0 &&
    college?.college_navbars?.[0]?.title?.replace(/\s+/g, '-')?.toLowerCase();

  const collegeUrl = isOnlineCollege
    ? `/colleges/${college?.college_url}`
    : `/colleges/${college?.college_url}/${navbar}`;

  return (
    <div className={`mb-3 rounded-lg border-[#DDDDDD] bg-white shadow-md ${className} w-full md:flex-1`}>
      <div className="flex w-full flex-col justify-center gap-2 rounded border px-1 py-2 shadow-sm md:flex-1 md:flex-row md:gap-x-4 md:px-2 lg:py-1.5">
        <div className="flex items-start gap-x-2.5 md:items-center md:justify-center md:gap-x-6 lg:pt-0">
          <div className="relative size-14 min-w-14 lg:size-24">
            <Image
              src={college?.logo?.url ?? FallbackLogo}
              alt={college?.college_name ? `${college.college_name} Logo` : 'College Logo'}
              width={200}
              height={80}
              className="rounded-sm object-cover"
            />
          </div>

          {/* Mobile Title & Info */}
          <div className="flex flex-col flex-wrap font-inter text-sm font-light leading-tight text-gray-500 md:hidden">
            <Link href={navbar ? collegeUrl : '#'}>
              <div>
                <p className="line-clamp-2 font-inter text-base leading-tight text-primary">
                  {college?.college_name}
                  <span className="inline-block font-inter">
                    , {college?.city?.publishedAt && college?.city?.city_name}
                  </span>
                </p>
              </div>
            </Link>

            <div className="flex flex-row flex-wrap">
              <span className="mr-2 text-xs">{college?.city?.publishedAt && college?.city?.city_name}</span>

              {college?.approved_by && (
                <span className="mx-2 font-inter text-xs">
                  {'|'} {college?.approved_by[0]?.organisation_name.slice(0, 7)} Approved
                </span>
              )}

              {college?.college_type?.college_type && (
                <span className="mx-2 font-inter text-xs">
                  {'|'} {college?.college_type?.college_type}
                </span>
              )}
              {college?.ranked_by && Array.isArray(college?.ranked_by) && college?.ranked_by.length > 0 && (
                <span className="ml-2 line-clamp-1 font-inter text-xs">
                  {'|'} {college?.ranked_by[0]?.ranking_body_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Title & Info */}
        <div className="flex w-full flex-1 flex-col justify-center gap-1 border-b-primary-light md:border-b lg:border-b-0 lg:border-r lg:border-r-primary-light">
          <Link href={navbar ? collegeUrl : '#'}>
            <div>
              <p className="line-clamp-2 hidden font-inter text-base leading-tight text-primary md:block">
                {college?.college_name} <span>{`[${college?.abbreviation}]`}</span>
                <span className="hidden font-inter md:inline-block">
                  , {college?.city?.publishedAt && college?.city?.city_name}
                </span>
              </p>
            </div>
          </Link>
          <div className="hidden flex-wrap items-center gap-x-4 font-inter text-xs font-light leading-tight md:flex">
            <p className="font-inter">
              {college?.city?.publishedAt && college?.city?.city_name},{' '}
              {college?.state?.publishedAt && college?.state?.state_name}
            </p>
            {college?.city?.publishedAt && college?.city?.city_name && college?.approved_by?.length > 0 && (
              <span>{'|'}</span>
            )}
            {college?.approved_by?.length > 0 && (
              <span>{`${college?.approved_by[0]?.organisation_name} Approved`}</span>
            )}
            {college?.approved_by?.length > 0 && college?.college_type && <span>{'|'}</span>}
            {college?.college_type && <span>{`${college?.college_type?.college_type}`} </span>}
            {college?.ranked_by?.length > 0 && college?.custom_ranking_2 && (
              <p>{`${college?.ranked_by[0]?.ranking_body_name} Ranking : ${college?.custom_ranking_2}`}</p>
            )}
          </div>
          <div className="flex flex-col items-stretch">
            <div className="mr-4 flex flex-col items-start gap-1 pr-4 md:flex-row md:items-center md:gap-5">
              {college?.average_fee?.average_fee && (
                <p className="font-inter text-sm text-[#B12704]">
                  {college?.average_fee?.average_fee}{' '}
                  <span className="font-inter text-xs font-normal text-black md:text-sm">- 1st year fees</span>
                </p>
              )}

              {/* Separator only if both fees and placement exist */}
              {college?.average_fee?.average_fee && college?.average_placement && (
                <p className="hidden md:contents">{'|'}</p>
              )}
              <p className="font-inter text-sm text-[#B12704]">
                {college?.average_placement ?? 'N/A'}{' '}
                <span className="font-inter text-xs font-normal text-black md:text-sm">- Avg Package</span>
              </p>

              {/* Additional Abroad College Fees Info */}
              {isAbroadCollege && (
                <>
                  {college?.average_tuition_fees && (
                    <p className="font-inter text-sm text-[#B12704]">
                      {college?.average_tuition_fees}{' '}
                      <span className="font-inter text-xs font-normal text-black md:text-sm">- Avg Tuition Cost</span>
                    </p>
                  )}
                  {college?.average_living_cost && (
                    <p className="font-inter text-sm text-[#B12704]">
                      {college?.average_living_cost}{' '}
                      <span className="font-inter text-xs font-normal text-black md:text-sm">- Avg Living Cost</span>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Abroad College Specific Info */}
            {isAbroadCollege && (
              <div className="flex items-center gap-x-4">
                {college?.acceptance_rate && (
                  <p className="rounded-xl border bg-slate-200 px-1 py-0.5 text-xs">
                    Acceptance Rate - {college?.acceptance_rate}
                  </p>
                )}
                {college?.acceptance_rate && <p>{'|'}</p>}
                <div className="flex  flex-wrap gap-1 text-xs">
                  Eligible exams -
                  {[
                    college?.is_gmat_accepted && 'GMAT',
                    college?.is_dualingo_accepted && 'Duolingo',
                    college?.is_gre_accepted && 'GRE',
                    college?.is_ielts_accepted && 'IELTS',
                    college?.is_pte_accepted && 'PTE',
                    college?.is_sat_accepted && 'SAT',
                    college?.is_toefl_accepted && 'TOEFL',
                  ]
                    .filter(Boolean)
                    .map((exam: string, index: number, arr: string[]) => (
                      <React.Fragment key={exam}>
                        <span className="text-primary">{exam}</span>
                        {index < arr.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                </div>
              </div>
            )}

            {/* Navigation Links */}
            {!isOnlineCollege && (
              <div className="mr-2 mt-1 flex justify-between">
                {/* Desktop Links */}
                <div className="hidden flex-wrap items-center gap-x-4 font-inter font-light leading-tight md:flex">
                  {college?.college_navbars?.slice(0, 7)?.map((nav: { title: string }, index: number) => {
                    return (
                      <Link
                        href={`/colleges/${id}/${nav?.title.replace(/\s+/g, '-').toLowerCase()}`}
                        key={index + nav?.title}
                        className="text-xs leading-tight text-primary"
                      >
                        {nav?.title} |
                      </Link>
                    );
                  })}
                  <Link
                    href={navbar ? `/colleges/${id}/${navbar}` : '#'}
                    className="text-xs font-semibold text-primary"
                  >
                    See More
                  </Link>
                </div>

                {/* Mobile Links */}
                <div className="flex flex-wrap items-center gap-x-1 font-inter font-light leading-tight md:hidden">
                  {college?.college_navbars?.slice(0, 4)?.map((nav: { title: string }, index: number) => {
                    return (
                      <Link
                        href={`/colleges/${id}/${nav?.title?.replace(/\s+/g, '-')?.toLowerCase()}`}
                        key={index + nav?.title}
                        className="text-xs leading-tight text-primary"
                      >
                        {nav?.title} |
                      </Link>
                    );
                  })}
                  <Link
                    href={navbar ? `/colleges/${id}/${navbar}` : '#'}
                    className="text-xs font-semibold text-primary"
                  >
                    See More
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions Section (Apply Now / Check Eligibility) */}
        <div className="inline-flex flex-row justify-center gap-2 sm:items-center md:flex-col md:gap-4">
          <div className="md:[130px] w-full lg:w-[160px]">
            <Button
              text="Apply Now"
              fontSize="text-xs"
              width="lg:w-36 md:w-28"
              align="text-center"
              filledColor="bg-primary"
              paddingY="py-1"
              btnClassName="w-full"
              onClick={handleOpen}
            />
          </div>
          <div className="md:[130px] block w-full lg:w-[160px]">
            <button
              className="w-full rounded-md border bg-white py-1 text-center text-xs tracking-tight md:w-fit lg:w-36"
              onClick={handleOpen}
            >
              <p>Check Eligibility</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
